import type { ButtonHTMLAttributes, MouseEvent } from 'react'
import type { NavigateOptions } from 'react-router'
import styled from 'styled-components'

import { useNavigate } from '../../hooks/useNavigate'

import { Color } from '../../theme/color'
import type { ColorKeys } from '../../theme/color'

type OmitTypeButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'>

interface ButtonProps extends OmitTypeButtonProps {
  type?: 'default' | 'primary' | 'dark' | 'line' | 'secondary'
  size?: 'compact' | 'small' | 'medium' | 'large'
  block?: boolean
  disabled?: boolean
  color?: ColorKeys
  href?: string
  routeProps?: NavigateOptions
}

export function Button({
  children,
  type = 'default',
  size = 'medium',
  disabled,
  href,
  routeProps,
  onClick,
  ...props
}: ButtonProps) {
  const navigate = useNavigate()

  const click = (e: MouseEvent<HTMLButtonElement>) => {
    onClick?.(e)
    if (href) {
      navigate(href, routeProps)
    }
  }

  return (
    <StyledButton $type={type} size={size} disabled={disabled} onClick={click} {...props}>
      {children}
    </StyledButton>
  )
}

export type { ButtonProps }

const StyledButton = styled.button<ButtonProps & { $type?: ButtonProps['type'] }>`
  align-items: center;
  justify-content: center;
  gap: 4px;
  opacity: 1 !important;
  outline: none;
  border: none;
  line-height: normal;
  cursor: pointer;
  ${({ block }) => (block ? 'display: flex; width: 100%;' : '')}
  ${({ size }) => {
    switch (size) {
      case 'compact':
        return `padding: 4px 6px; border-radius: 4px; font-size: 14px; font-weight: 500;`
      case 'small':
        return 'padding: 8px 24px; border-radius: 4px; font-size: 16px; font-weight: 500;'
      case 'medium':
        return 'padding: 12px; border-radius: 8px; font-size: 18px; font-weight: 600;'
      case 'large':
        return 'padding: 13.5px; border-radius: 8px; font-size: 18px; font-weight: 600;'
    }
  }}
  ${({ $type, size, color }) => {
    switch ($type) {
      case 'default':
        return `
          background-color: ${Color.gray_2} !important;
          color: ${Color.text_primary};
          svg > path {
            fill: ${Color.text_primary};
          }
        `
      case 'primary':
        if (size === 'compact')
          return `
          background-color: ${Color.primary_2} !important;
          color: ${Color.primary_5};
          svg > path {
            fill: ${Color.primary_5};
          }
        `
        return `
          background-color: ${Color.primary_5} !important;
          color: ${Color.text_white};
          svg > path {
            fill: ${Color.text_white};
          }
        `
      case 'dark':
        return `
          background-color: ${Color.gray_7} !important;
          color: ${Color.text_white};
          svg > path {
            fill: ${Color.text_white};
          }
        `
      case 'line':
        return `
          border: 1px solid ${Color.gray_3};
          background-color: ${Color.white} !important;
          color: ${color ?? Color.text_primary};
        `
      case 'secondary':
        return `
          background-color: ${Color.primary_2} !important;
          color: ${Color.primary_5};
        `
    }
  }}
  &:disabled {
    background-color: ${Color.gray_2} !important;
    color: ${Color.gray_4};
    cursor: auto;
  }
`
